import { Box, Container, Typography } from '@mui/material';
import { useEffect } from 'react';
import { ShieldCheck, Lock, FileSearch } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const sections = [
  {
    icon: ShieldCheck,
    number: '01',
    title: 'Legal Civil Works Disclaimer',
    content: `At Clarence Homes, we understand the importance of legal compliance and the protection of privacy. The information provided on this page is intended for general understanding and does not constitute legal advice. It is crucial to seek professional legal counsel to ensure the creation of a comprehensive Privacy Policy tailored to your specific business operations and obligations.`,
  },
  {
    icon: Lock,
    number: '02',
    title: 'Privacy Policy - Essential Information',
    content: `In the context of our civil works operations, our Privacy Policy serves as a transparent disclosure of how we collect, utilize, and manage data pertinent to our clients, partners, and visitors. It includes our commitment to safeguarding privacy and outlines the mechanisms we employ to ensure data protection. Adhering to the legal requirements of various jurisdictions is fundamental, and we advise aligning your Privacy Policy with the applicable laws and regulations.`,
  },
  {
    icon: FileSearch,
    number: '03',
    title: 'Key Elements Covered in Our Privacy Policy',
    content: `Our Privacy Policy comprehensively addresses the types of data collected, the methods of data acquisition, the purpose behind data collection, data sharing practices with third parties, and the mechanisms for individuals to exercise their privacy rights. Furthermore, it outlines our approach to handling minors' data and encompasses various other essential aspects. For detailed insights, refer to our resource 'Crafting a Comprehensive Privacy Policy'.`,
  },
];

function Privacy() {
  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f8f8', overflow: 'hidden' }}>

      {/* Hero */}
      <Box sx={{
        backgroundColor: 'var(--primary-color)',
        py: { xs: 7, md: 11 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-80px', right: '-80px',
          width: '320px', height: '320px',
          borderRadius: '50%',
          border: '70px solid rgba(192,174,93,0.08)',
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: '-90px', left: '-50px',
          width: '280px', height: '280px',
          borderRadius: '50%',
          border: '55px solid rgba(192,174,93,0.06)',
        },
      }}>
        <Typography sx={{ fontSize: '12px', color: 'var(--primary-text-color)', letterSpacing: 4, textTransform: 'uppercase', mb: 2 }}>
          Clarence Homes
        </Typography>
        <Typography sx={{ fontSize: { xs: '34px', md: '54px' }, fontWeight: 800, color: '#fff', letterSpacing: 2, lineHeight: 1.1 }}>
          PRIVACY POLICY
        </Typography>
        <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2.5 }} />
        <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'rgba(255,255,255,0.6)', maxWidth: 520, mx: 'auto' }}>
          Your privacy matters to us. Learn how we collect, use, and protect your information.
        </Typography>
      </Box>

      {/* Gold notice bar */}
      <Box sx={{ backgroundColor: 'var(--primary-text-color)', py: 2 }}>
        <Container maxWidth="xl">
          <Typography sx={{ fontSize: '13px', color: 'var(--primary-color)', fontWeight: 700, textAlign: 'center', letterSpacing: 0.3 }}>
            This page is for informational purposes only and does not constitute legal advice. Please consult a qualified legal professional.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>

        {/* Section cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Box
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 80}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    transform: 'translateY(-6px)',
                  }
                }}
              >
                {/* Left accent strip + number */}
                <Box sx={{
                  width: { xs: '100%', sm: '90px' },
                  minHeight: { sm: '100%' },
                  backgroundColor: 'var(--primary-color)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  py: { xs: 2.5, sm: 0 }, px: 2, gap: 1.5,
                  flexShrink: 0,
                }}>
                  <Icon size={26} color="var(--primary-text-color)" />
                  <Typography sx={{ fontSize: '22px', fontWeight: 800, color: 'rgba(255,255,255,0.2)', lineHeight: 1 }}>
                    {section.number}
                  </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 3, md: 4.5 }, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: '4px', height: '22px', backgroundColor: 'var(--primary-text-color)', borderRadius: '2px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 700, color: 'var(--primary-color)' }}>
                      {section.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95 }}>
                    {section.content}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

      </Container>
    </Box>
  );
}

export default Privacy;
